import { useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { SectionHeading, TableCustom, WrapperAdvancedSearch } from "../../components/common"
import { LISTAS } from "../../consts/listas"
import { getDateEndOfDaySerialize, getDateToString } from "../../utils/convert"
import { useManagedContext, useNav } from "../../components/hooks"
import { useUsuarios } from "../../components/hooks/useUsuarios"
import { ServerRequest } from "../../utils/apiweb"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { onRequestError, onSearchNoSuccess, onRequestProcessError } from "../../utils/requests"
import { VALIDATION_MESSAGE } from "../../consts/validationMessage"
import { APPCONFIG } from "../../app.config"
const USUARIO_CACHE_KEY = 'Auditoria_UsuarioCache'

const AlertasView = () => {
    const navigate = useNav()
    const { setIsLoading } = useManagedContext()

    const [alertas, setAlertas] = useState([])
    const [tableVisible, setTableVisible] = useState(false)
    const [searchMessage, setSearchMessage] = useState(null)

    const [getNombreUsuario, resolveUsuarios, resolveAllUsuarios] = useUsuarios({ key: USUARIO_CACHE_KEY })
    const usuarioCache = useSelector((state) => state.memo.cache[USUARIO_CACHE_KEY]?.value)

    const usuarioOptions = useMemo(() => {
        return Object.entries(usuarioCache ?? {})
            .filter(([, nombre]) => nombre)
            .map(([id, nombre]) => ({ id: Number(id), nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
    }, [usuarioCache])

    const searchRef = useRef()

    const Search = () => {
        const { filters } = searchRef.current
        let resolveSearch
        const searchDone = new Promise((resolve) => { resolveSearch = resolve })

        setIsLoading(true)

        setSearchMessage(null)

        const params = `/filter` +
            `?idTipoAlerta=${filters.idTipoAlerta ?? ''}` +
            `&idModulo=${filters.idModulo ?? ''}` +
            `&id=${filters.id ?? ''}` +
            `&idUsuario=${filters.idUsuario ?? ''}` +
            `&fechaDesde=${filters.fechaDesde ?? ''}` +
            `&fechaHasta=${encodeURIComponent(getDateEndOfDaySerialize(filters.fechaHasta))}` +
            `&origen=${filters.origen ?? ''}` +
            `&mensaje=${filters.mensaje ?? ''}` +
            `&contenido=${filters.contenido ?? ''}`

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.ALERTA,
            params,
            null,
            res => {
                res.json().then(data => {
                    setAlertas(data)
                    resolveUsuarios(data.map(e => e.idUsuario))
                    setTableVisible(true)
                    setIsLoading(false)
                    resolveSearch(true)
                }).catch(err => {
                    onRequestProcessError(err)
                    setIsLoading(false)
                    resolveSearch(false)
                })
            },
            res => {
                onSearchNoSuccess(res, () => {
                    setAlertas([])
                    setTableVisible(false)
                    setSearchMessage(VALIDATION_MESSAGE.TOO_MANY_RESULTS)
                })
                setIsLoading(false)
                resolveSearch(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
                resolveSearch(false)
            },
        )

        return searchDone
    }

    function ValidFilters(aplicados) {
        const contenidoAplicado = aplicados.some(x => x.field === 'contenido')
        const otrosFiltrosAplicados = aplicados.filter(x => x.field !== 'contenido').length
        if (contenidoAplicado && otrosFiltrosAplicados < 2) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Para buscar por contenido, aplicá al menos dos filtros.')
            return false
        }
        return true
    }

    return <>
        <SectionHeading titles={[{ title: 'Alertas' }]} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                message={searchMessage}
                ref={searchRef}
                onSearch={Search}
                onValidation={ValidFilters}
                filters={[
                    {
                        title: 'Tipo de alerta',
                        field: 'idTipoAlerta',
                        type: 'selectLista',
                        size: 3,
                        options: [{ value: 0, label: 'Todos' }, ...LISTAS.TIPO_ALERTA.map(tipo => ({ value: Number(tipo.id), label: tipo.nombre }))],
                        emptyValue: 0,
                    },
                    {
                        title: 'Módulo',
                        field: 'idModulo',
                        type: 'selectLista',
                        size: 3,
                        options: [{ value: 0, label: 'Todos' }, ...LISTAS.MODULO.map(tipo => ({ value: Number(tipo.id), label: tipo.nombre }))],
                        emptyValue: 0,
                    },
                    { title: 'Código', field: 'id', type: 'string', size: 2, searchOnEnter: true, },
                    {
                        title: 'Usuario/a',
                        field: 'idUsuario',
                        type: 'entity',
                        size: 4,
                        entidad: 'Usuario',
                        data: usuarioOptions,
                        columns: [{ Header: 'Nombre', accessor: 'nombre', width: '95%' }],
                        onOpen: () => resolveAllUsuarios(),
                    },
                    { title: 'Fecha desde', field: 'fechaDesde', type: 'date', size: 3 },
                    { title: 'Fecha hasta', field: 'fechaHasta', minValueField: 'fechaDesde', type: 'date', size: 3 },
                    { title: 'Mensaje', field: 'mensaje', type: 'string', size: 6, searchOnEnter: true, },
                    { title: 'Contenido', field: 'contenido', type: 'string', size: 12, searchOnEnter: true, },
                ]}
            />

            {tableVisible && <TableCustom
                className={'TableCustomBase m-top-20'}
                data={alertas}
                messageEmpty="No se encontraron resultados"
                columns={[
                    { Header: 'Código', accessor: 'id', width: '15%' },
                    { Header: 'Alerta', accessor: 'idTipoAlerta', Cell: LISTAS.TIPO_ALERTA.getCell(), width: '12%' },
                    { Header: 'Módulo', accessor: 'idModulo', Cell: LISTAS.MODULO.getCell(), width: '15%' },
                    { Header: 'Usuario/a', accessor: 'idUsuario', Cell: ({ value }) => getNombreUsuario(value), width: '10%' },
                    { Header: 'Fecha/Hora', accessor: 'fecha', Cell: ({ value }) => getDateToString(value, true), width: '20%' },
                    { Header: 'Mensaje', accessor: 'mensaje', },
                ]}
                avmr={{
                    onView: (item) => {
                        const url = `${APPCONFIG.SITE.WEBAPP}alerta/${item.id}`
                        window.open(url)
                    }
                }}
            />}
        </section>
    </>
}

export default AlertasView
